import { z } from "zod";
import { RFQStatusSchema, type RFQStatus } from "../../../clients/Purchaser/RFQ";
import { CircleDashed, ArchiveX, CircleCheckBig } from "lucide-react";
import { type ReactNode } from "react";
import CircleProgress from "../../Common/CustomIcons/CircleProgress";
import { Capitalized } from "../../../util/formatting";
import { type SetAttributeFilter } from "../../Attributes/types";
import { attributeFiltersParam, withAttributeFilter } from "../../Attributes/attributeFilterParam";
import { DATE_RANGE_PRESET_KEYS, resolveDateRange } from "../../../util/dateRangePresets";
import { urlParam, useFilterParams, type FilterValues, type SortOption, type SortState } from "../../Common/Filters";

export type RFQStatusMetadata = {
    title: string;
    icon: ReactNode;
    colorIcon: ReactNode;
    bgClass: string;
    borderClass: string;
};

export const RFQStatusMeta = {
    prettyName(status: RFQStatus | undefined) {
        return (status ?? "")
            .split("_")
            .map(str => Capitalized(str))
            .join(" ");
    },
    properties(status: RFQStatus): RFQStatusMetadata {
        const title = this.prettyName(status);
        switch (status) {
            case "IN_PROGRESS":
                return {
                    title,
                    icon: <CircleProgress.Stone />,
                    colorIcon: <CircleProgress.Bronze />,
                    bgClass: "bg-bronze-100",
                    borderClass: "border border-bronze-100 hover:border-bronze-200",
                };
            case "DRAFT":
                return {
                    title,
                    icon: <CircleDashed className="h-4 w-4 stroke-stone-500" />,
                    colorIcon: <CircleDashed className="h-4 w-4 stroke-stone-500" />,
                    bgClass: "bg-stone-100",
                    borderClass: "border border-stone-100 hover:border-stone-200",
                };
            case "CLOSED":
                return {
                    title,
                    icon: <CircleCheckBig className="h-4 w-4 stroke-green-700" />,
                    colorIcon: <CircleCheckBig className="h-4 w-4 stroke-green-700" />,
                    bgClass: "bg-green-50",
                    borderClass: "border border-green-50 hover:border-green-200",
                };
            case "ARCHIVED":
                return {
                    title,
                    icon: <ArchiveX className="h-4 w-4 text-stone-400" />,
                    colorIcon: <ArchiveX className="h-4 w-4 text-stone-400" />,
                    bgClass: "bg-stone-100",
                    borderClass: "border border-stone-100 hover:border-bronze-200",
                };
        }
    },
};

export const RFQSortSchema = z.enum(["LastActivity", "CreatedDate", "AssignedTo", "CreatedBy", "QuoteCount", "ID"]);
export const RFQSortOrderSchema = z.enum(["asc", "desc"]);
export type RFQSort = z.infer<typeof RFQSortSchema>;
export type RFQSortOrder = z.infer<typeof RFQSortOrderSchema>;

// Each direction matches the API's default for that sort, so sortOrder is only kept in the URL when flipped.
const RFQ_SORT_OPTIONS: readonly SortOption<RFQSort>[] = [
    { value: "LastActivity", label: "Last Activity", direction: "desc" },
    { value: "CreatedDate", label: "Created Date", direction: "desc" },
    { value: "AssignedTo", label: "Assigned To", direction: "asc" },
    { value: "CreatedBy", label: "Created By", direction: "asc" },
    { value: "QuoteCount", label: "Quote Count", direction: "desc" },
    { value: "ID", label: "RFQ ID", direction: "desc" },
];

/** The RFQ list's sort, kept in the URL. Read it with useSortParams, or parseSortParams in a route loader. */
export const RFQ_SORT = { key: "sort", directionKey: "sortOrder", options: RFQ_SORT_OPTIONS, fallback: "LastActivity" as const };

/**
 * Every filter the RFQ list keeps in the URL, and how each is read. Read them with useRFQListFilters, or
 * parseFilterParams in a route loader. The API's own schema in purchaser-api's RFQList.ts mirrors these.
 */
export const RFQ_LIST_FILTERS = {
    params: {
        // These are not technically "filters" but need to be stored in the search params
        view: urlParam.text,
        search: urlParam.text,

        // Everything below represents a "filter"
        unread: urlParam.flag,
        quotes: urlParam.flag,
        // The first status is the one the status tab shows. An empty list, kept as an empty param, means
        // every status; a missing param means none has been chosen yet, so the list falls back to its default tab.
        statuses: { read: urlParam.listOf(RFQStatusSchema.options), keepEmpty: true },
        assignedTo: urlParam.numberList,
        createdBy: urlParam.numberList,
        updatedStart: urlParam.number,
        updatedEnd: urlParam.number,
        createdStart: urlParam.number,
        createdEnd: urlParam.number,
        // A date preset is kept by name, so it keeps rolling: "This Week" means this week whenever the link is
        // opened. rfqListQuery works out its start and end.
        updatedPreset: urlParam.oneOf(DATE_RANGE_PRESET_KEYS),
        createdPreset: urlParam.oneOf(DATE_RANGE_PRESET_KEYS),
        labels: urlParam.numberList,
        labelGroups: urlParam.numberList,
        vendorRelationships: urlParam.numberList,
        attributeFilters: attributeFiltersParam,
    },
};

export type RFQListFilters = FilterValues<typeof RFQ_LIST_FILTERS.params>;

/** What /v2/rfqs accepts: the list's filters with any date preset worked out, plus its sort and page. */
export type RFQListQuery = Omit<RFQListFilters, "updatedPreset" | "createdPreset"> & {
    sort?: RFQSort;
    sortOrder?: RFQSortOrder;
    page?: number;
    limit?: number;
};

/**
 * The /v2/rfqs query for the list's filters and sort. A date preset is worked out as of today, and the sort's
 * direction is always sent, since the API's own default differs for sorts it works out itself, such as quote count.
 */
export function rfqListQuery(filters: RFQListFilters, sort: Pick<SortState<RFQSort>, "by" | "direction">): RFQListQuery {
    const { updatedPreset, createdPreset, ...rest } = filters;
    const updated = resolveDateRange({ preset: updatedPreset, start: filters.updatedStart, end: filters.updatedEnd });
    const created = resolveDateRange({ preset: createdPreset, start: filters.createdStart, end: filters.createdEnd });
    return {
        ...rest,
        updatedStart: updated.start,
        updatedEnd: updated.end,
        createdStart: created.start,
        createdEnd: created.end,
        sort: sort.by,
        sortOrder: sort.direction,
    };
}

export const MY_RFQS_DEFAULT_STATUSES: RFQStatus[] = ["IN_PROGRESS", "DRAFT"];

// The statuses the current tab shows. With no status tab picked, such as after every status was unchecked,
// that's the list's default In Progress tab.
export function getDefaultStatuses(filters: RFQListFilters): RFQStatus[] {
    if (filters.view === "my") return MY_RFQS_DEFAULT_STATUSES;
    return filters.statuses?.length ? [filters.statuses[0]] : ["IN_PROGRESS"];
}

// Every status was unchecked, so the list shows RFQs of all statuses.
export function isShowingAllStatuses(filters: RFQListFilters): boolean {
    return filters.statuses?.length === 0;
}

export function getAdditionalStatuses(filters: RFQListFilters): RFQStatus[] {
    const defaults = getDefaultStatuses(filters);
    return (filters.statuses ?? []).filter(s => !defaults.includes(s));
}

export function useRFQListFilters() {
    const { filters, setFilters, menuState } = useFilterParams(RFQ_LIST_FILTERS);

    const attributeFilters = filters.attributeFilters ?? [];
    const setAttributeFilter: SetAttributeFilter = (filter, remove) =>
        setFilters({ attributeFilters: withAttributeFilter(attributeFilters, filter, remove) });

    const clearFilters = () => {
        setFilters({
            unread: undefined,
            quotes: undefined,
            assignedTo: undefined,
            createdBy: undefined,
            updatedStart: undefined,
            updatedEnd: undefined,
            createdStart: undefined,
            createdEnd: undefined,
            updatedPreset: undefined,
            createdPreset: undefined,
            labels: undefined,
            labelGroups: undefined,
            vendorRelationships: undefined,
            attributeFilters: undefined,
            statuses: getDefaultStatuses(filters),
        });
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (getAdditionalStatuses(filters).length > 0 || isShowingAllStatuses(filters)) count += 1;
        if (filters.unread === true) count += 1;
        if (filters.quotes === true) count += 1;
        if (filters.assignedTo) count += 1;
        if (filters.createdBy) count += 1;
        if (filters.updatedPreset || filters.updatedStart || filters.updatedEnd) count += 1;
        if (filters.createdPreset || filters.createdStart || filters.createdEnd) count += 1;
        if (filters.labels) count += 1;
        if (filters.labelGroups) count += 1;
        if (filters.vendorRelationships) count += 1;
        if (attributeFilters.length > 0) count += 1;
        return count;
    };

    return { filters, setFilters, clearFilters, getActiveFilterCount, attributeFilters, setAttributeFilter, menuState };
}
